
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

interface PendingUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  created_at: string;
  approval_status: string;
  role: string;
}

export const useAccountApprovals = () => {
  const [pendingLecturers, setPendingLecturers] = useState<PendingUser[]>([]);
  const [pendingStudents, setPendingStudents] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchPendingAccounts = async () => {
    try {
      setLoading(true);
      console.log('Fetching pending accounts...');

      // First, get all pending profiles
      const { data: pendingProfiles, error: pendingError } = await supabase
        .from('profiles')
        .select('*')
        .eq('approval_status', 'pending');

      if (pendingError) {
        console.error('Error fetching pending profiles:', pendingError);
        throw pendingError;
      }

      if (!pendingProfiles || pendingProfiles.length === 0) {
        setPendingLecturers([]);
        setPendingStudents([]);
        console.log('Successfully fetched pending accounts:', {
          lecturers: 0,
          students: 0
        });
        return;
      }

      // Get roles for all pending users
      const pendingIds = pendingProfiles.map(p => p.id);
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('user_id', pendingIds);

      if (rolesError) {
        console.error('Error fetching user roles:', rolesError);
        throw rolesError;
      }

      // Create a map of user_id to role
      const roleMap = new Map();
      userRoles?.forEach(ur => {
        roleMap.set(ur.user_id, ur.role);
      });

      // Categorize users by role
      const lecturersList: PendingUser[] = [];
      const studentsList: PendingUser[] = [];
      const noRoleList: PendingUser[] = [];

      pendingProfiles.forEach(profile => {
        const userRole = roleMap.get(profile.id);
        const userWithRole = {
          ...profile,
          role: userRole || 'no_role'
        };

        if (userRole === 'lecturer') {
          lecturersList.push(userWithRole);
        } else if (userRole === 'student') {
          studentsList.push(userWithRole);
        } else {
          // Users without roles - show in students section for now
          noRoleList.push(userWithRole);
        }
      });

      // Combine students list with users who have no role (they might be students who didn't get role assigned)
      const allStudents = [...studentsList, ...noRoleList];
      
      setPendingLecturers(lecturersList);
      setPendingStudents(allStudents);
      
      console.log('Successfully fetched pending accounts:', {
        lecturers: lecturersList.length,
        students: studentsList.length
      });

    } catch (error: any) {
      console.error('Error fetching pending accounts:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց բեռնել սպասող հաշիվները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string, role: string) => {
    try {
      setActionLoading(`approve-${userId}`);
      console.log('Starting approval process for user:', { userId, role });
      
      // Check if current user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('Նույնականացում անհրաժեշտ է');
      }

      console.log('Current user authenticated:', user.id);

      // First, update the profile status directly
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          approval_status: 'approved',
          approved_by: user.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }

      console.log('Profile updated successfully for user:', userId);

      toast({
        title: "Հաջողություն",
        description: `${role === 'lecturer' ? 'Դասախոսի' : 'Ուսանողի'} հաշիվը հաստատվեց`
      });

      // Remove the user from the appropriate list immediately for better UX
      if (role === 'lecturer') {
        setPendingLecturers(prev => prev.filter(user => user.id !== userId));
      } else {
        setPendingStudents(prev => prev.filter(user => user.id !== userId));
      }

      // No need to manually refresh - the real-time subscription will handle it

    } catch (error: any) {
      console.error('Error approving user:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց հաստատել հաշիվը",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: string, role: string) => {
    try {
      setActionLoading(`reject-${userId}`);
      console.log('Starting rejection process for user:', { userId, role });
      
      // Check if current user is authenticated
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('Նույնականացում անհրաժեշտ է');
      }

      console.log('Current user authenticated:', user.id);

      // Update the profile status directly
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          approval_status: 'rejected',
          approved_by: user.id,
          approved_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw updateError;
      }

      console.log('Profile rejected successfully for user:', userId);

      toast({
        title: "Հաջողություն",
        description: `${role === 'lecturer' ? 'Դասախոսի' : 'Ուսանողի'} հաշիվը մերժվեց`
      });

      // Remove the user from the appropriate list immediately for better UX
      if (role === 'lecturer') {
        setPendingLecturers(prev => prev.filter(user => user.id !== userId));
      } else {
        setPendingStudents(prev => prev.filter(user => user.id !== userId));
      }

      // No need to manually refresh - the real-time subscription will handle it

    } catch (error: any) {
      console.error('Error rejecting user:', error);
      toast({
        title: "Սխալ",
        description: error.message || "Չհաջողվեց մերժել հաշիվը",
        variant: "destructive"
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Set up real-time subscription for profile changes
  useEffect(() => {
    const channel = supabase
      .channel('profile-approval-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          console.log('Profile change detected:', payload);
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          
          // Only react to approval status changes
          if (oldRecord?.approval_status === 'pending' && newRecord?.approval_status !== 'pending') {
            console.log('Approval status changed from pending to:', newRecord.approval_status);
            // Immediately remove from state without waiting
            const userId = newRecord.id;
            setPendingLecturers(prev => prev.filter(user => user.id !== userId));
            setPendingStudents(prev => prev.filter(user => user.id !== userId));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchPendingAccounts();
  }, []);

  return {
    pendingLecturers,
    pendingStudents,
    loading,
    actionLoading,
    handleApprove,
    handleReject,
    refreshData: fetchPendingAccounts
  };
};
